import type { EventType, EventHandler } from '../types';

/**
 * Simple event emitter for SDK events
 */
export class EventEmitter {
  private listeners: Map<EventType, Set<EventHandler>> = new Map();
  private onceHandlers: Map<EventHandler, EventHandler> = new Map(); // Maps wrapper -> original

  /**
   * Subscribe to an event
   */
  on<T = any>(event: EventType, handler: EventHandler<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Subscribe to an event (fires only once)
   */
  once<T = any>(event: EventType, handler: EventHandler<T>): () => void {
    const onceHandler: EventHandler<T> = (data) => {
      this.onceHandlers.delete(onceHandler);
      this.off(event, onceHandler);
      handler(data);
    };
    this.onceHandlers.set(onceHandler, handler);
    return this.on(event, onceHandler);
  }

  /**
   * Unsubscribe from an event
   */
  off(event: EventType, handler: EventHandler): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
      // Also clean up from onceHandlers if it was a once handler
      this.onceHandlers.delete(handler);
    }
  }

  /**
   * Emit an event
   */
  emit<T = any>(event: EventType, data?: T): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch {
          // Silently ignore handler errors
        }
      });
    }
  }

  /**
   * Remove all listeners for an event (or all events if no event specified)
   */
  removeAllListeners(event?: EventType): void {
    if (event) {
      // Clean up once handlers for this event
      const handlers = this.listeners.get(event);
      if (handlers) {
        handlers.forEach((handler) => {
          this.onceHandlers.delete(handler);
        });
      }
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
      this.onceHandlers.clear();
    }
  }

  /**
   * Get listener count for an event
   */
  listenerCount(event: EventType): number {
    return this.listeners.get(event)?.size || 0;
  }
}
